import { createContext, useContext, useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { EMPRESAS, PROJETOS } from "@/data/mockData";
import { v360EmpresaAPI } from "@/services/api";
import i18n from "@/i18n";
import { getV360Ctx, setV360Ctx, scopeWorkspaceId } from "@/lib/impersonation";

const TenantContext = createContext(null);

// Item B — chave `s360_projeto_ativo__<workspace>` (JSON {id, nome}).
// Lida/escrita de forma síncrona (localStorage puro) para permitir seleção
// provisória instantânea do projeto ativo, sem esperar hidratação do backend.
function readProjetoCache() {
  try {
    const ws = scopeWorkspaceId();
    if (!ws) return null;
    const raw = localStorage.getItem(`s360_projeto_ativo__${ws}`);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      return parsed?.id ? { id: parsed.id, nome: parsed.nome || undefined } : null;
    } catch {
      return { id: raw }; // formato legado: valor era o id puro (string)
    }
  } catch {
    return null;
  }
}
function writeProjetoCache(projeto) {
  try {
    const ws = scopeWorkspaceId();
    if (!ws) return;
    const key = `s360_projeto_ativo__${ws}`;
    if (projeto?.id) localStorage.setItem(key, JSON.stringify({ id: projeto.id, nome: projeto.nome || null }));
    else localStorage.removeItem(key);
  } catch { /* noop */ }
}

// Aplica a preferência de idioma da empresa/workspace (Phase 8.37). Persiste
// em `v360.idioma` no localStorage para o LanguageDetector do i18next pegar
// mesmo em reloads antes do TenantProvider hidratar.
function applyEmpresaLocale(idioma) {
  const lng = idioma === "en-US" ? "en-US" : "pt-BR";
  try { localStorage.setItem("v360.idioma", lng); } catch { /* noop */ }
  if (i18n.language !== lng) i18n.changeLanguage(lng);
}

/**
 * TenantProvider — Centraliza o "escopo" multi-tenant do usuário
 * (workspace -> empresa -> projeto -> papel).
 *
 * Em produção:
 *  - workspaceId vem do JWT (claim `tenant_id`)
 *  - empresa/projeto vêm do backend via /api/me/context
 *  - papel é derivado do JWT (claim `role`)
 *
 * Quando a URL contém `?empresa=<uuid>` (rota V360), a empresaAtual é
 * sobrescrita com o perfil real (razão social, setor etc.) — assim o
 * chrome do app (Header, sidebar) reflete a empresa em diagnóstico
 * em vez do mock EMPRESAS[0].
 */
export function TenantProvider({ children }) {
  const [workspaceId, setWorkspaceId] = useState("ws_consultoria_valora");
  const [papelAtual, setPapelAtual] = useState("Gerente de Projeto");
  const [empresaAtual, setEmpresaAtual] = useState(EMPRESAS[0]);
  // Item B — lê o cache local de forma SÍNCRONA no primeiro render (reload
  // com sessão já persistida): exibe a última seleção conhecida na hora,
  // em vez de aguardar 3-7s pela hidratação do backend. `hydrateFromBackend`
  // (readSession abaixo) corrige depois se houver discrepância.
  const [projetoAtual, _setProjetoAtual] = useState(() => {
    const cached = readProjetoCache();
    if (cached) return cached;
    try {
      const s = JSON.parse(localStorage.getItem("app_session") || "null");
      if (s?.workspace_id) return null; // tenant real sem seleção salva ainda
    } catch { /* noop */ }
    return PROJETOS[0]; // primeiro load, sem sessão — mock legado
  });
  // Toda alteração de projetoAtual passa por aqui e persiste no localStorage
  // (`s360_projeto_ativo__<workspace>`), aceitando tanto valor direto quanto
  // função updater (mesma API do setState nativo).
  const setProjetoAtual = useCallback((updater) => {
    _setProjetoAtual(prev => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      writeProjetoCache(next);
      return next;
    });
  }, []);
  const [moduloAtual, setModuloAtual] = useState("valora360"); // sprints360 | valora360 — V360 é default do primeiro acesso (Phase 7.40)
  const [locale, setLocale] = useState("pt-BR");
  // Phase 7.37 — sessão do usuário logado (pós setup-senha). null = mock legado.
  const [currentUser, setCurrentUser] = useState(null);
  // Jun/2026 — marca se o chrome atual veio do modo ghost (imp_session).
  const ghostAplicadoRef = useRef(false);
  // Phase 7.41 — capacidades do plano contratado (default true = mock legado)
  const [hasV360, setHasV360]         = useState(true);
  const [hasSprints, setHasSprints]   = useState(true);
  // Phase 8.5 — capacidade "PERSONALIZAÇÃO" (KPIs & Volumetrias customizados).
  // Default true durante fase de aprovação/testes; futuramente amarrado ao plano.
  const [hasCustomMetrics, setHasCustomMetrics] = useState(true);
  // Phase 12.27 — código do plano contratado (ex.: 'S360-CONSULTING'). Usado
  // pelo S360ConsultContext para auto-ativar o modo consultoria (RULE 3).
  const [planoCodigo, setPlanoCodigo] = useState(null);
  const [planoCustomizado, setPlanoCustomizado] = useState(false);
  // Phase 12.30 — status do workspace (ativo/suspenso/cancelado/demo) e papel
  // para governar bloqueio de navegação e default pós-login.
  const [workspaceStatus, setWorkspaceStatus] = useState("ativo");
  const [workspaceAtivo, setWorkspaceAtivo] = useState(true);

  // Phase 10.32 — sincroniza `moduloAtual` com a rota atual quando o
  // usuário navega para uma rota exclusiva de um dos apps. Isso garante
  // que telas neutras (Usuários, Configurações) usem o app "correto"
  // como default, mesmo que o usuário nunca tenha clicado no AppSwitcher.
  const { pathname: currentPath } = useLocation();
  useEffect(() => {
    const inV360Route    = currentPath.startsWith("/app/v360");
    const inSprintsRoute = [
      "/app/dashboard", "/app/projetos", "/app/recomendacoes",
      "/app/sprint-board", "/app/gantt", "/app/schedule", "/app/jornada",
      "/app/calendario", "/app/calendar", "/app/documentos", "/app/documents",
      "/app/reunioes", "/app/painel-semanal", "/app/weekly", "/app/sprints360",
      "/app/atividades", "/app/s360-empresas",
    ].some(p => currentPath.startsWith(p));
    if (inV360Route)         setModuloAtual("valora360");
    else if (inSprintsRoute) setModuloAtual("sprints360");
    // rotas neutras (/app/usuarios, /app/configuracoes, /app/subscricoes, /app/welcome)
    // NÃO alteram o moduloAtual — herdam o último app "visitado".
  }, [currentPath]);

  const switchEmpresa = useCallback((e) => {
    setEmpresaAtual(e);
    const next = PROJETOS.find(p => p.empresaId === e?.id);
    if (next) setProjetoAtual(next);
  }, []);

  const clearSession = useCallback(() => {
    // Phase 7.45 → 12.28 — Logout limpo: apaga TODOS os resquícios de sessão
    // para impedir que a próxima conexão herde dados do tenant anterior.
    // Cobertura:
    //   • app_session, imp_session, v360.empresaId        (auth + Wizard)
    //   • s360_consult_ativa_id_v2                        (empresa simulada ativa)
    //   • s360_mgc_projeto_ativo                          (projeto ativo do MGC)
    //   • s360_state_v1__<workspace_id> / sprints360__*   (bucket Sprints360)
    //   • s360.ldv.cache*                                 (cache Leitura da Val)
    //   • mockUserName, v360.idioma                       (preferências de UI)
    //   • ?empresa=… / ?workspace=…                       (params na URL)
    // Também chama supabase.auth.signOut() para encerrar o JWT no cliente.
    (async () => {
      try {
        const { getSupabase } = await import("@/lib/supabase");
        const sb = await getSupabase();
        await sb.auth.signOut();
      } catch (e) { console.warn("[clearSession] supabase.signOut falhou:", e); }
    })();
    try {
      // Chaves exatas herdadas de sessões passadas.
      localStorage.removeItem("app_session");
      localStorage.removeItem("imp_session");
      localStorage.removeItem("v360.empresaId");
      // Phase 12.28 — Elimina resquícios cross-tenant que sobreviviam ao logout
      // e faziam a próxima sessão exibir dados de projeto/empresa simulada da
      // sessão anterior (ver auditoria multi-tenant, Feb/2026).
      localStorage.removeItem("s360_consult_ativa_id_v2");
      localStorage.removeItem("s360_mgc_projeto_ativo");
      localStorage.removeItem("mockUserName");
      localStorage.removeItem("v360.idioma");
      // Prefixos: itera e apaga todas as chaves que casam.
      // - `sprints360__`     — pedido explícito da auditoria (mesmo que o
      //   código atual use `s360_state_v1__`, cobrimos ambos para robustez).
      // - `s360_state_v1__`  — bucket real do Sprints360Context por workspace.
      // - `s360.ldv.cache`   — cache da Leitura da Val (multi-projeto).
      const PREFIXES_TO_CLEAR = ["sprints360__", "s360_state_v1__", "s360.ldv.cache"];
      const toRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (!k) continue;
        if (PREFIXES_TO_CLEAR.some(p => k.startsWith(p))) toRemove.push(k);
      }
      toRemove.forEach(k => { try { localStorage.removeItem(k); } catch { /* noop */ } });
      // Dispara AMBOS os eventos para o hook usePerfilAutosave e outros
      // ouvintes reagirem imediatamente (multi-tab compatível via `storage`).
      window.dispatchEvent(new CustomEvent("app-session-changed"));
      window.dispatchEvent(new CustomEvent("imp-session-changed"));
    } catch { /* noop */ }
    try {
      const u = new URL(window.location.href);
      let changed = false;
      if (u.searchParams.has("empresa"))    { u.searchParams.delete("empresa");    changed = true; }
      if (u.searchParams.has("workspace"))  { u.searchParams.delete("workspace");  changed = true; }
      if (changed) window.history.replaceState({}, "", u.toString());
    } catch { /* noop */ }
    setCurrentUser(null);
  }, []);

  // Lê app_session do localStorage e hidrata state (multi-aba compatível)
  useEffect(() => {
    const readSession = () => {
      try {
        const raw = localStorage.getItem("app_session");
        const s = raw ? JSON.parse(raw) : null;
        if (!s || !s.user_id) { setCurrentUser(null); return; }
        setCurrentUser({
          id:        s.user_id,
          nome:      s.user_nome,
          email:     s.user_email,
          papel:     s.user_papel,
          isSuperAdm: !!s.is_super_adm,
          iniciais:  (s.user_nome || "").split(" ").filter(Boolean).slice(0, 2).map(w => w[0]).join("").toUpperCase() || "U",
          avatar:    null,
        });
        if (s.workspace_id) setWorkspaceId(s.workspace_id);
        // Phase 7.41 — capacidades do plano
        const hV = s.has_v360 !== false;   // default true se ausente
        const hS = s.has_sprints360 !== false;
        setHasV360(hV);
        setHasSprints(hS);
        // Phase 8.5 — capacidade PERSONALIZAÇÃO (default true se ausente).
        setHasCustomMetrics(s.has_custom_metrics !== false);
        // Phase 12.27 — plano contratado (usado pelo S360ConsultContext)
        setPlanoCodigo(s.plano_codigo || null);
        // Plano Customizado · E1 — flag de regime customizado
        setPlanoCustomizado(s.plano_customizado === true);
        // Phase 12.30 — status do workspace (bloqueio de navegação).
        setWorkspaceStatus(s.workspace_status || "ativo");
        setWorkspaceAtivo(s.workspace_ativo !== false);
        // Phase 12.31 — aplica idioma padrão do país do workspace assim que
        // a sessão hidrata (antes exigia empresa em URL ?empresa=...).
        if (s.idioma_padrao) applyEmpresaLocale(s.idioma_padrao);
        // Força módulo compatível com o plano — S360 se V360 não incluído.
        if (!hV && hS) setModuloAtual("sprints360");
        else if (hV && !hS) setModuloAtual("valora360");
        if (s.workspace_nome) {
          setEmpresaAtual(prev => ({
            ...prev,
            id: s.empresa_perfil_id || prev.id,
            nome: s.workspace_nome,
            __live: !!s.empresa_perfil_id,
          }));
          // Phase 12.4 — Ao entrar num tenant REAL, zera o `projetoAtual`
          // legado (PROJETOS[0] mock) para o Header não sugerir projeto de
          // outro cliente. O usuário seleciona um projeto real assim que
          // criar. Só limpamos se o projeto atual for do mock (id começa com
          // "p" seguido de dígito, padrão do PROJETOS mock — projetos reais
          // usam UUID).
          if (s.empresa_perfil_id) {
            setProjetoAtual(prev => {
              const looksMock = prev && typeof prev.id === "string" && /^p\d+$/.test(prev.id);
              if (!prev || looksMock) {
                // Item B — antes de cair para null, tenta a última seleção
                // conhecida deste workspace (evita "Nenhum projeto selecionado").
                return readProjetoCache() || null;
              }
              return prev;
            });
          }
        }
        // Papel do usuário → label pt-BR amigável
        const map = { super_adm_empresa: "Adm. Master", adm_empresa: "Administrador", executivo: "Executivo", consultor: "Consultor", gerente_projeto: "Gerente de Projeto", executor: "Executor" };
        if (s.user_papel) setPapelAtual(map[s.user_papel] || s.user_papel);

        // Jun/2026 — MODO GHOST tem precedência sobre a sessão do admin.
        // Sem isso, ao sair das rotas V360 (que carregam `?empresa=`), o
        // Sprints360 caía no tenant DEMO (Vector Logística) e mostrava
        // projetos mock em vez dos projetos reais do cliente impersonado.
        try {
          const impRaw = localStorage.getItem("imp_session");
          const imp = impRaw ? JSON.parse(impRaw) : null;
          const expirado = imp?.expires_at && new Date(imp.expires_at).getTime() < Date.now();
          const ctx = (s.is_super_adm && !s.workspace_id) ? getV360Ctx() : null;
          const efetivo = (imp?.empresa_perfil_id && !expirado)
            ? { empresa_perfil_id: imp.empresa_perfil_id, workspace_id: imp.workspace_id,
                nome: imp.workspace_nome }
            : (ctx?.workspace_id
                ? { empresa_perfil_id: ctx.empresa_perfil_id, workspace_id: ctx.workspace_id,
                    nome: ctx.workspace_nome }
                : null);
          if (efetivo) {
            if (efetivo.workspace_id) setWorkspaceId(efetivo.workspace_id);
            ghostAplicadoRef.current = true;
            setEmpresaAtual(prev => ({
              ...prev,
              id: efetivo.empresa_perfil_id,
              nome: efetivo.nome || prev.nome,
              __live: true,
            }));
          } else if (ghostAplicadoRef.current) {
            // Saiu do modo ghost — devolve o chrome ao contexto do próprio
            // usuário para não exibir o cliente impersonado por engano.
            ghostAplicadoRef.current = false;
            setEmpresaAtual(s.empresa_perfil_id
              ? prev => ({ ...prev, id: s.empresa_perfil_id, nome: s.workspace_nome, __live: true })
              : EMPRESAS[0]);
          }
        } catch { /* noop */ }
      } catch {
        setCurrentUser(null);
      }
    };
    readSession();
    window.addEventListener("app-session-changed", readSession);
    window.addEventListener("imp-session-changed", readSession);
    window.addEventListener("storage", readSession);
    return () => {
      window.removeEventListener("app-session-changed", readSession);
      window.removeEventListener("imp-session-changed", readSession);
      window.removeEventListener("storage", readSession);
    };
  }, []);

  // Sincroniza empresaAtual com ?empresa=<uuid> da URL (V360)
  const location = useLocation();
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const empresaId = sp.get("empresa");
    if (!empresaId) return;
    // Já carregada — evita refetch
    if (empresaAtual?.id === empresaId && empresaAtual?.__live) return;
    let cancelled = false;
    (async () => {
      try {
        const perfil = await v360EmpresaAPI.getPerfil(empresaId);
        if (cancelled || !perfil) return;
        // Jun/2026 — Super Admin não tem workspace próprio. Resolvemos o
        // workspace dono deste perfil e guardamos como contexto, senão o S360
        // cai no tenant DEMO (mostrava "Vector Logística" com projetos mock).
        try {
          const s = JSON.parse(localStorage.getItem("app_session") || "null");
          if (s?.is_super_adm && !s?.workspace_id) {
            const ws = await v360EmpresaAPI.getWorkspace(empresaId).catch(() => null);
            if (ws?.workspace_id && getV360Ctx()?.workspace_id !== ws.workspace_id) {
              setV360Ctx({ empresa_perfil_id: empresaId, workspace_id: ws.workspace_id,
                           workspace_nome: ws.nome });
            }
          }
        } catch { /* noop */ }
        const nomeResolved = perfil.nome_fantasia || perfil.razao_social || "Empresa em diagnóstico";
        setEmpresaAtual({
          id: perfil.id,
          nome: nomeResolved,
          razao_social: perfil.razao_social,
          setor: perfil.setor_nome,
          porte: perfil.porte_nome,
          estagio: perfil.estagio_nome,
          regiao: [perfil.estado, perfil.pais_nome].filter(Boolean).join(" / "),
          idioma_padrao: perfil.idioma_padrao || "pt-BR",
          __live: true,
        });
        // Phase 8.37 — aplica idioma padrão da empresa ao workspace atual
        applyEmpresaLocale(perfil.idioma_padrao);
        // Hidrata workspace_nome no imp_session se ainda estiver vazio
        try {
          const raw = localStorage.getItem("imp_session");
          if (raw) {
            const imp = JSON.parse(raw);
            if (!imp.workspace_nome && imp.empresa_perfil_id === perfil.id) {
              imp.workspace_nome = nomeResolved;
              localStorage.setItem("imp_session", JSON.stringify(imp));
              window.dispatchEvent(new CustomEvent("imp-session-changed"));
            }
          }
        } catch { /* noop */ }
      } catch { /* silencioso — mantém fallback do mock */ }
    })();
    return () => { cancelled = true; };
  }, [location.search]);

  const value = useMemo(() => ({
    workspaceId,
    papelAtual, setPapelAtual,
    empresaAtual, setEmpresaAtual: switchEmpresa,
    projetoAtual, setProjetoAtual,
    moduloAtual, setModuloAtual,
    locale, setLocale,
    currentUser,           // Phase 7.37 — null = usuário mockado do Header
    clearSession,
    hasV360, hasSprints,   // Phase 7.41 — capacidades do plano contratado
    hasCustomMetrics,      // Phase 8.5 — capacidade PERSONALIZAÇÃO
    planoCodigo,           // Phase 12.27 — código do plano (ex.: 'S360-CONSULTING')
    planoCustomizado,      // Plano Customizado · E1 — regime customizado ativo
    workspaceStatus,       // Phase 12.30 — ativo | suspenso | cancelado | demo
    workspaceAtivo,        // Phase 12.30 — flag booleana
  }), [workspaceId, papelAtual, empresaAtual, projetoAtual, moduloAtual, locale, switchEmpresa, currentUser, clearSession, hasV360, hasSprints, hasCustomMetrics, planoCodigo, planoCustomizado, workspaceStatus, workspaceAtivo]);

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export const useTenant = () => {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error("useTenant must be used inside TenantProvider");
  return ctx;
};

// Backwards-compat alias (legacy useApp from earlier scaffold)
export const useApp = useTenant;
